import { useContext, useState, useEffect, useRef } from "react";
import { ThemeContext } from "../../../App";
import styles from "./Header.module.css";

function Header() {
    const { isDarkMode, toggleTheme } = useContext(ThemeContext);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    // References for ripple effect
    const themeButtonRef = useRef<HTMLButtonElement>(null);
    const mobileMenuButtonRef = useRef<HTMLButtonElement>(null);

    // Track scroll position to apply styling
    useEffect(() => {
        const handleScroll = () => {
            const isScrolled = window.scrollY > 10;
            if (isScrolled !== scrolled) {
                setScrolled(isScrolled);
            }
        };

        window.addEventListener("scroll", handleScroll);

        // Call once to set initial state
        handleScroll();

        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, [scrolled]);

    const toggleMobileMenu = () => {
        setMobileMenuOpen(!mobileMenuOpen);
        addRippleEffect(mobileMenuButtonRef.current);
    };

    const handleNavClick = () => {
        setMobileMenuOpen(false);
    };

    // Add ripple effect to button
    const addRippleEffect = (button: HTMLButtonElement | null) => {
        if (!button) return;

        button.classList.remove(styles.ripple);
        // Trigger reflow
        void button.offsetWidth;
        button.classList.add(styles.ripple);

        // Clean up after animation completes
        setTimeout(() => {
            button.classList.remove(styles.ripple);
        }, 600);
    };

    // Theme toggle with ripple
    const handleThemeToggle = () => {
        toggleTheme();
        addRippleEffect(themeButtonRef.current);
    };

    return (
        <header
            className={`${styles.header} ${scrolled ? styles.scrolled : ""}`}>
            <div className="container">
                <div className="d-flex align-items-center justify-content-between py-3">
                    <a href="/" className={styles.logo}>
                        <img
                            src="/BotPombal.png"
                            alt="Logo da Competição"
                            className="me-2"
                        />
                    </a>

                    <div className="d-flex align-items-center">
                        {/* Desktop Navigation */}
                        <nav className={styles.desktopNav}>
                            <a href="#info" className={styles.navLink}>
                                Informações
                            </a>
                            <a href="#challenges" className={styles.navLink}>
                                Desafios
                            </a>
                            <a href="#carousel" className={styles.navLink}>
                                Galeria
                            </a>
                            <a href="#location" className={styles.navLink}>
                                Localização
                            </a>
                            <a href="#faq" className={styles.navLink}>
                                Perguntas
                            </a>
                            <a href="#sponsors" className={styles.navLink}>
                                Patrocinadores
                            </a>
                        </nav>

                        <button
                            ref={themeButtonRef}
                            onClick={handleThemeToggle}
                            className={`${styles.themeToggle} ${styles.animatedButton}`}
                            aria-label={
                                isDarkMode
                                    ? "Mudar para modo claro"
                                    : "Mudar para modo escuro"
                            }>
                            {isDarkMode ? "🌙" : "☀️"}
                        </button>

                        {/* Mobile Menu Button */}
                        <button
                            ref={mobileMenuButtonRef}
                            className={`${styles.mobileMenuBtn} ${
                                mobileMenuOpen ? styles.active : ""
                            } ${styles.animatedButton}`}
                            aria-label="Menu"
                            onClick={toggleMobileMenu}>
                            <span></span>
                            <span></span>
                            <span></span>
                        </button>
                    </div>
                </div>

                {/* Mobile Navigation */}
                <div
                    className={`${styles.mobileNav} ${
                        mobileMenuOpen ? styles.open : ""
                    }`}>
                    <nav>
                        <a
                            href="#info"
                            className={styles.mobileNavLink}
                            onClick={handleNavClick}>
                            Informações
                        </a>
                        <a
                            href="#challenges"
                            className={styles.mobileNavLink}
                            onClick={handleNavClick}>
                            Desafios
                        </a>
                        <a
                            href="#carousel"
                            className={styles.mobileNavLink}
                            onClick={handleNavClick}>
                            Galeria
                        </a>
                        <a
                            href="#location"
                            className={styles.mobileNavLink}
                            onClick={handleNavClick}>
                            Localização
                        </a>
                        <a
                            href="#faq"
                            className={styles.mobileNavLink}
                            onClick={handleNavClick}>
                            Perguntas
                        </a>
                        <a
                            href="#sponsors"
                            className={styles.mobileNavLink}
                            onClick={handleNavClick}>
                            Patrocinadores
                        </a>
                    </nav>
                </div>
            </div>
        </header>
    );
}

export default Header;
